/**
 * Typed-ish fetchers for the connector browse surface (M4):
 * /api/v1/integration/veeva/* and /api/v1/integration/blob/*.
 *
 * The backend returns plain dicts for browse reads (mirrors external
 * data — see the user-export precedent), so the response shapes here are
 * hand-written interfaces, not OpenAPI-generated types. Downloads stream
 * bytes, so they bypass `apiFetch` (which assumes JSON) and use the
 * `downloadFile` helper to trigger a browser save.
 */

import { apiFetch } from "./apiFetch";

// --- Veeva ----------------------------------------------------------------

export interface DocumentSummary {
  id: string;
  name: string | null;
  type: string | null;
  status: string | null;
  lifecycle: string | null;
  major_version: number | null;
  minor_version: number | null;
  version_id: string | null;
  format: string | null;
  size_bytes: number | null;
  filename: string | null;
  created_by: string | null;
  created_date: string | null;
  modified_date: string | null;
}

export interface DocumentListResult {
  items: DocumentSummary[];
}

export interface QueryResult {
  records: Record<string, unknown>[];
  total: number | null;
}

export function veevaQuery(
  token: string,
  connectionId: string,
  vql: string,
  /** Add a `<field>__label` column beside each reference field's id. */
  resolveLabels = false,
): Promise<QueryResult> {
  return apiFetch<QueryResult>(
    `/api/v1/integration/veeva/${encodeURIComponent(connectionId)}/query`,
    { method: "POST", token, json: { vql, resolve_labels: resolveLabels } },
  );
}

export function veevaListDocuments(
  token: string,
  connectionId: string,
  vql: string,
): Promise<DocumentListResult> {
  return apiFetch<DocumentListResult>(
    `/api/v1/integration/veeva/${encodeURIComponent(connectionId)}/documents`,
    { method: "POST", token, json: { vql } },
  );
}

export function veevaDocumentContentUrl(connectionId: string, documentId: string): string {
  return `/api/v1/integration/veeva/${encodeURIComponent(connectionId)}/documents/${encodeURIComponent(documentId)}/content`;
}

export interface BulkDownloadOutcome {
  count: number;
  matched: number;
  truncated: boolean;
}

/**
 * Download every document matched by `vql` as a single zip (backend streams
 * it). Returns the counts from the response headers so the UI can warn when
 * the server capped the result set. Bulk egress — audited server-side.
 */
export async function veevaDownloadAll(
  token: string,
  connectionId: string,
  vql: string,
): Promise<BulkDownloadOutcome> {
  const res = await fetch(
    `/api/v1/integration/veeva/${encodeURIComponent(connectionId)}/documents/download-all`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ vql }),
    },
  );
  if (!res.ok) {
    throw new Error(`Download failed (HTTP ${res.status})`);
  }
  saveBlob(await res.blob(), "documents.zip");
  return {
    count: Number(res.headers.get("X-Documents-Count") ?? 0),
    matched: Number(res.headers.get("X-Documents-Matched") ?? 0),
    truncated: (res.headers.get("X-Documents-Truncated") ?? "false") === "true",
  };
}

// --- CSV export (client-side, zero-dependency) ----------------------------

/** Serialise dynamic rows to CSV. Columns are the union of all row keys. */
export function recordsToCsv(rows: Record<string, unknown>[]): string {
  if (rows.length === 0) return "";
  const cols = Array.from(
    rows.reduce((set, r) => {
      Object.keys(r).forEach((k) => set.add(k));
      return set;
    }, new Set<string>()),
  );
  const esc = (v: unknown): string => {
    if (v === null || v === undefined) return "";
    const s = typeof v === "object" ? JSON.stringify(v) : String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = cols.map(esc).join(",");
  const body = rows.map((r) => cols.map((c) => esc(r[c])).join(",")).join("\r\n");
  return `${header}\r\n${body}`;
}

/** Build a CSV from `rows` and trigger a browser save. */
export function downloadCsv(filename: string, rows: Record<string, unknown>[]): void {
  // Prepend a UTF-8 BOM so Excel opens accented characters correctly.
  const blob = new Blob(["﻿", recordsToCsv(rows)], { type: "text/csv;charset=utf-8" });
  saveBlob(blob, filename);
}

// --- Veeva File Staging (the vault's "FTP" area) --------------------------

export interface FileStagingItem {
  name: string;
  path: string;
  kind: string; // "file" | "folder"
  size: number | null;
  modified_date: string | null;
}

export function veevaStagingList(
  token: string,
  connectionId: string,
  opts: { path?: string | null; recursive?: boolean; cursor?: string | null } = {},
): Promise<{ items: FileStagingItem[]; resolved_path: string; next_cursor: string | null }> {
  const params = new URLSearchParams();
  if (opts.path) params.set("path", opts.path);
  if (opts.recursive) params.set("recursive", "true");
  if (opts.cursor) params.set("cursor", opts.cursor);
  const qs = params.toString();
  return apiFetch(
    `/api/v1/integration/veeva/${encodeURIComponent(connectionId)}/files/list${qs ? `?${qs}` : ""}`,
    { token },
  );
}

export function veevaStagingContentUrl(connectionId: string, path: string): string {
  return `/api/v1/integration/veeva/${encodeURIComponent(connectionId)}/files/content?path=${encodeURIComponent(path)}`;
}

export function veevaStagingUpload(
  token: string,
  connectionId: string,
  path: string,
  file: File,
  overwrite = false,
): Promise<FileStagingItem> {
  const form = new FormData();
  form.set("path", path);
  form.set("overwrite", String(overwrite));
  form.set("file", file);
  return apiFetch(`/api/v1/integration/veeva/${encodeURIComponent(connectionId)}/files/upload`, {
    method: "POST",
    token,
    body: form,
  });
}

export function veevaStagingCreateFolder(
  token: string,
  connectionId: string,
  path: string,
): Promise<FileStagingItem> {
  return apiFetch(`/api/v1/integration/veeva/${encodeURIComponent(connectionId)}/files/folder`, {
    method: "POST",
    token,
    json: { path },
  });
}

export function veevaStagingDelete(
  token: string,
  connectionId: string,
  path: string,
  reason: string,
  recursive = false,
): Promise<void> {
  const qs =
    `?path=${encodeURIComponent(path)}` +
    `&recursive=${recursive}` +
    `&reason=${encodeURIComponent(reason)}`;
  return apiFetch<void>(
    `/api/v1/integration/veeva/${encodeURIComponent(connectionId)}/files${qs}`,
    { method: "DELETE", token },
  );
}

/**
 * Bulk-download every file under a staging folder as one zip (server-side,
 * bounded + audited as `veeva_file.bulk_downloaded`). `path` omitted → the
 * user's staging home. Returns the scope so the caller can warn on a cap.
 */
export async function veevaStagingDownloadAll(
  token: string,
  connectionId: string,
  path?: string | null,
  recursive = true,
): Promise<BulkDownloadOutcome> {
  const params = new URLSearchParams();
  if (path) params.set("path", path);
  params.set("recursive", String(recursive));
  const res = await fetch(
    `/api/v1/integration/veeva/${encodeURIComponent(connectionId)}/files/download-all?${params.toString()}`,
    { method: "POST", headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) {
    throw new Error(`Download failed (HTTP ${res.status})`);
  }
  saveBlob(await res.blob(), "veeva-files.zip");
  return {
    count: Number(res.headers.get("X-Files-Count") ?? 0),
    matched: Number(res.headers.get("X-Files-Matched") ?? 0),
    truncated: (res.headers.get("X-Files-Truncated") ?? "false") === "true",
  };
}

export function veevaStagingRename(
  token: string,
  connectionId: string,
  path: string,
  newParent: string,
  newName: string,
): Promise<{ status: string }> {
  return apiFetch(`/api/v1/integration/veeva/${encodeURIComponent(connectionId)}/files/rename`, {
    method: "PUT",
    token,
    json: { path, new_parent: newParent, new_name: newName },
  });
}

// --- Veeva metadata (VQL query builder) -----------------------------------

export interface ObjectSummary {
  name: string;
  label: string | null;
}

export interface FieldDefinition {
  name: string;
  label: string | null;
  type: string | null;
  required: boolean | null;
  max_length: number | null;
  editable: boolean | null;
  hidden: boolean | null;
  system_attribute: boolean | null;
  /** For reference fields, the object this field points at (else null). */
  related_object: string | null;
  /** Vault enforces uniqueness — what an upsert can match on. */
  unique?: boolean | null;
  /** For a picklist field, the picklist's name (several fields can share one). */
  picklist?: string | null;
  /** A multi-value picklist takes several values at once. */
  multi_value?: boolean | null;
  /** The allowed values, when they were asked for — what a value map maps onto. */
  picklist_values?: { name: string; label: string | null }[];
}

/** List the vault's queryable objects (documents + vobjects). */
export function veevaListObjects(
  token: string,
  connectionId: string,
): Promise<{ items: ObjectSummary[] }> {
  return apiFetch<{ items: ObjectSummary[] }>(
    `/api/v1/integration/veeva/${encodeURIComponent(connectionId)}/objects`,
    { token },
  );
}

/** List one object's fields — `documents` is routed to its special endpoint server-side. */
export function veevaDescribeObject(
  token: string,
  connectionId: string,
  objectName: string,
): Promise<{ fields: FieldDefinition[] }> {
  return apiFetch<{ fields: FieldDefinition[] }>(
    `/api/v1/integration/veeva/${encodeURIComponent(connectionId)}/describe-object`,
    { method: "POST", token, json: { object_name: objectName } },
  );
}

// --- Blob -----------------------------------------------------------------

export interface ContainerSummary {
  name: string;
  last_modified: string | null;
}

export interface BlobSummary {
  name: string;
  size_bytes: number;
  last_modified: string | null;
  content_type: string | null;
}

export function blobListContainers(
  token: string,
  connectionId: string,
  nameStartsWith?: string | null,
): Promise<{ items: ContainerSummary[] }> {
  const qs = nameStartsWith ? `?name_starts_with=${encodeURIComponent(nameStartsWith)}` : "";
  return apiFetch<{ items: ContainerSummary[] }>(
    `/api/v1/integration/blob/${encodeURIComponent(connectionId)}/containers${qs}`,
    { token },
  );
}

export function blobListFiles(
  token: string,
  connectionId: string,
  container: string,
  prefix?: string | null,
): Promise<{ items: BlobSummary[] }> {
  const params = new URLSearchParams({ container });
  if (prefix) params.set("prefix", prefix);
  return apiFetch<{ items: BlobSummary[] }>(
    `/api/v1/integration/blob/${encodeURIComponent(connectionId)}/files?${params.toString()}`,
    { token },
  );
}

export interface ReadTableResult {
  rows: Record<string, unknown>[];
  format: string;
  delimiter: string | null;
  row_count: number;
  truncated: boolean;
}

/**
 * Download + parse a tabular blob (CSV/TSV/JSON/JSONL/XLSX) into rows — the
 * Blob Source node's Test preview. Uses the same parser the pipeline engine
 * runs, so the preview matches a real run. `sheet` selects an XLSX worksheet;
 * `delimiter` overrides CSV/TSV column separation (empty → auto-detect).
 */
export function blobReadTable(
  token: string,
  connectionId: string,
  container: string,
  blobName: string,
  opts: { sheet?: string | null; delimiter?: string | null } = {},
): Promise<ReadTableResult> {
  return apiFetch<ReadTableResult>(
    `/api/v1/integration/blob/${encodeURIComponent(connectionId)}/read-table`,
    {
      method: "POST",
      token,
      json: {
        container,
        blob_name: blobName,
        sheet: opts.sheet || null,
        delimiter: opts.delimiter || null,
      },
    },
  );
}

/** Where a Blob Source document-list row's file lives (the reserved `_file`
 *  column). `null` when the row names no listed blob. */
export interface BlobFileRef {
  connection_id: string;
  container: string;
  path: string;
  etag: string | null;
  name: string;
  size_bytes: number;
  content_type: string | null;
}

export interface BlobDocumentsResult {
  /** The first `limit` matched rows, each with `_file`. */
  rows: Record<string, unknown>[];
  /** The `blobs` table's columns (fixed ones + `meta_<key>`). */
  columns: string[];
  /** How many rows the query matched — what a run would emit. */
  row_count: number;
  /** The query matched more than a run's 10,000-row cap. */
  truncated: boolean;
  /** Files read from the listing (folders excluded). */
  listed: number;
  /** The listing stopped at its 50,000-blob cap. */
  listing_truncated: boolean;
  /** Matched rows that carry a `_file` reference. */
  linked: number;
}

/**
 * The Blob Source document-list preview: list the container (under `prefix`)
 * as the table `blobs` and run one SELECT over it — the same function a run
 * calls. Not audited (a listing); a refused or failing query is a 400.
 */
export function blobQueryDocuments(
  token: string,
  connectionId: string,
  container: string,
  query: string,
  opts: { prefix?: string | null; limit?: number } = {},
): Promise<BlobDocumentsResult> {
  return apiFetch<BlobDocumentsResult>(
    `/api/v1/integration/blob/${encodeURIComponent(connectionId)}/query-documents`,
    {
      method: "POST",
      token,
      json: {
        container,
        prefix: opts.prefix || null,
        query,
        limit: opts.limit ?? 50,
      },
    },
  );
}

export function blobFileContentUrl(connectionId: string, container: string, blob: string): string {
  const params = new URLSearchParams({ container, blob });
  return `/api/v1/integration/blob/${encodeURIComponent(connectionId)}/files/content?${params.toString()}`;
}

export function blobUploadFile(
  token: string,
  connectionId: string,
  container: string,
  blobName: string,
  file: File,
  overwrite = false,
): Promise<{ container: string; blob_name: string; size_bytes: number; etag: string | null }> {
  const form = new FormData();
  form.set("container", container);
  form.set("blob_name", blobName);
  form.set("overwrite", String(overwrite));
  form.set("file", file);
  return apiFetch(`/api/v1/integration/blob/${encodeURIComponent(connectionId)}/files`, {
    method: "POST",
    token,
    body: form,
  });
}

export function blobCreateContainer(
  token: string,
  connectionId: string,
  container: string,
  reason: string,
): Promise<{ container: string }> {
  const qs = `?container=${encodeURIComponent(container)}&reason=${encodeURIComponent(reason)}`;
  return apiFetch<{ container: string }>(
    `/api/v1/integration/blob/${encodeURIComponent(connectionId)}/containers${qs}`,
    { method: "POST", token },
  );
}

/**
 * Download every blob in a container (optionally under a prefix) as one zip
 * (backend streams it). Returns the header counts so the UI can warn when the
 * server capped the set. Bulk egress — audited server-side.
 */
export async function blobDownloadAll(
  token: string,
  connectionId: string,
  container: string,
  prefix?: string | null,
): Promise<BulkDownloadOutcome> {
  const params = new URLSearchParams({ container });
  if (prefix) params.set("prefix", prefix);
  const res = await fetch(
    `/api/v1/integration/blob/${encodeURIComponent(connectionId)}/files/download-all?${params.toString()}`,
    { method: "POST", headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) {
    throw new Error(`Download failed (HTTP ${res.status})`);
  }
  saveBlob(await res.blob(), `${container}.zip`);
  return {
    count: Number(res.headers.get("X-Files-Count") ?? 0),
    matched: Number(res.headers.get("X-Files-Matched") ?? 0),
    truncated: (res.headers.get("X-Files-Truncated") ?? "false") === "true",
  };
}

export function blobDeleteContainer(
  token: string,
  connectionId: string,
  container: string,
  reason: string,
): Promise<void> {
  const qs = `?confirm=true&reason=${encodeURIComponent(reason)}`;
  return apiFetch<void>(
    `/api/v1/integration/blob/${encodeURIComponent(connectionId)}/containers/${encodeURIComponent(container)}${qs}`,
    { method: "DELETE", token },
  );
}

/** Delete one blob. Write permission + a required reason (audited `file.deleted`). */
export function blobDeleteFile(
  token: string,
  connectionId: string,
  container: string,
  blob: string,
  reason: string,
): Promise<void> {
  const params = new URLSearchParams({ container, blob, reason });
  return apiFetch<void>(
    `/api/v1/integration/blob/${encodeURIComponent(connectionId)}/files?${params.toString()}`,
    { method: "DELETE", token },
  );
}

// --- S3 --------------------------------------------------------------------
// The same shape as the Blob fetchers with S3 vocabulary (bucket / key).
// Bucket create + delete are deliberately absent — the backend offers no such
// route (ADR-0021 §5).

export interface BucketSummary {
  name: string;
  /** S3 reports bucket creation, not last-modified — see the backend model. */
  created: string | null;
}

export interface S3ObjectSummary {
  name: string;
  size_bytes: number;
  last_modified: string | null;
  /** Always null from a listing; S3 only reveals it on download. */
  content_type: string | null;
}

export function s3ListBuckets(
  token: string,
  connectionId: string,
  nameStartsWith?: string | null,
): Promise<{ items: BucketSummary[] }> {
  const qs = nameStartsWith ? `?name_starts_with=${encodeURIComponent(nameStartsWith)}` : "";
  return apiFetch<{ items: BucketSummary[] }>(
    `/api/v1/integration/s3/${encodeURIComponent(connectionId)}/buckets${qs}`,
    { token },
  );
}

export function s3ListFiles(
  token: string,
  connectionId: string,
  bucket: string,
  prefix?: string | null,
): Promise<{ items: S3ObjectSummary[] }> {
  const params = new URLSearchParams({ bucket });
  if (prefix) params.set("prefix", prefix);
  return apiFetch<{ items: S3ObjectSummary[] }>(
    `/api/v1/integration/s3/${encodeURIComponent(connectionId)}/files?${params.toString()}`,
    { token },
  );
}

/** Download + parse a tabular object into rows — the S3 Source node's Test
 *  preview. Same parser the pipeline engine runs (see `blobReadTable`). */
export function s3ReadTable(
  token: string,
  connectionId: string,
  bucket: string,
  key: string,
  opts: { sheet?: string | null; delimiter?: string | null } = {},
): Promise<ReadTableResult> {
  return apiFetch<ReadTableResult>(
    `/api/v1/integration/s3/${encodeURIComponent(connectionId)}/read-table`,
    {
      method: "POST",
      token,
      json: {
        bucket,
        key,
        sheet: opts.sheet || null,
        delimiter: opts.delimiter || null,
      },
    },
  );
}

export function s3FileContentUrl(connectionId: string, bucket: string, key: string): string {
  const params = new URLSearchParams({ bucket, key });
  return `/api/v1/integration/s3/${encodeURIComponent(connectionId)}/files/content?${params.toString()}`;
}

export function s3UploadFile(
  token: string,
  connectionId: string,
  bucket: string,
  key: string,
  file: File,
  overwrite = false,
): Promise<{ bucket: string; key: string; size_bytes: number; etag: string | null }> {
  const form = new FormData();
  form.set("bucket", bucket);
  form.set("key", key);
  form.set("overwrite", String(overwrite));
  form.set("file", file);
  return apiFetch(`/api/v1/integration/s3/${encodeURIComponent(connectionId)}/files`, {
    method: "POST",
    token,
    body: form,
  });
}

/**
 * Download every object in a bucket (optionally under a prefix) as one zip.
 * Returns the header counts so the UI can warn when the server capped the set.
 * Bulk egress — audited server-side.
 */
export async function s3DownloadAll(
  token: string,
  connectionId: string,
  bucket: string,
  prefix?: string | null,
): Promise<BulkDownloadOutcome> {
  const params = new URLSearchParams({ bucket });
  if (prefix) params.set("prefix", prefix);
  const res = await fetch(
    `/api/v1/integration/s3/${encodeURIComponent(connectionId)}/files/download-all?${params.toString()}`,
    { method: "POST", headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) {
    throw new Error(`Download failed (HTTP ${res.status})`);
  }
  saveBlob(await res.blob(), `${bucket}.zip`);
  return {
    count: Number(res.headers.get("X-Files-Count") ?? 0),
    matched: Number(res.headers.get("X-Files-Matched") ?? 0),
    truncated: (res.headers.get("X-Files-Truncated") ?? "false") === "true",
  };
}

/** Delete one object. Write permission + a required reason (audited `file.deleted`). */
export function s3DeleteFile(
  token: string,
  connectionId: string,
  bucket: string,
  key: string,
  reason: string,
): Promise<void> {
  const params = new URLSearchParams({ bucket, key, reason });
  return apiFetch<void>(
    `/api/v1/integration/s3/${encodeURIComponent(connectionId)}/files?${params.toString()}`,
    { method: "DELETE", token },
  );
}

// --- Download helper -------------------------------------------------------

/** Trigger a browser "save as" for an in-memory blob. */
function saveBlob(blob: Blob, name: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}

/**
 * Fetch a byte-stream endpoint with the bearer token and trigger a browser
 * save. Browse downloads are not JSON, so they can't go through `apiFetch`.
 *
 * `preferredName` (the caller's metadata-derived name, e.g. the Veeva
 * document filename or the blob name) wins — it's more reliable than the
 * server's Content-Disposition, which for Veeva can be just the document id.
 * The CD is parsed only as a fallback (handles `filename*=` + `filename=`).
 */
export async function downloadFile(
  token: string,
  url: string,
  preferredName?: string,
): Promise<void> {
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    throw new Error(`Download failed (HTTP ${res.status})`);
  }
  const blob = await res.blob();
  let name = preferredName?.trim();
  if (!name) {
    const disposition = res.headers.get("content-disposition") ?? "";
    const captured = /filename\*?=(?:[^']*'[^']*')?"?([^";]+)"?/i.exec(disposition)?.[1];
    name = captured ? decodeURIComponent(captured).replace(/"/g, "") : "download";
  }
  saveBlob(blob, name);
}

export interface DocumentTypeNode {
  name: string;
  label: string;
}

export interface DocumentTypeLevel {
  /** The level below this one: a type's subtypes, a subtype's classifications. */
  items: DocumentTypeNode[];
  /** The lifecycles this level allows. Empty at the top (no type chosen yet). */
  lifecycles: DocumentTypeNode[];
}

/** One level of Vault's document type → subtype → classification tree.
 *
 *  Called per level rather than walking the whole tree: a RIM vault has a dozen
 *  types with dozens of subtypes each, and fetching them all to fill one picker
 *  would be a hundred requests for a choice made once. */
export function veevaDocumentTypes(
  token: string,
  connectionId: string,
  body: { type_name?: string; subtype_name?: string } = {},
): Promise<DocumentTypeLevel> {
  return apiFetch<DocumentTypeLevel>(
    `/api/v1/integration/veeva/${encodeURIComponent(connectionId)}/document-types`,
    { method: "POST", token, json: body },
  );
}
